import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  BookCopy,
  CircleAlert,
  FilePlus2,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Printer,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, BASE, getToken } from '@/lib/api';
import { LEDGER_STATUS_FILTER_OPTIONS, getLedgerDocumentPolicy } from '@/lib/finance-workflow-policy';
import { useToast } from '@/lib/toast';
import { useEntityData } from '@/hooks/useEntityData';
import { useViewTemplate } from '@/lib/use-view-template';
import type { EntityMeta } from '@/types/entity';
import { FinanceWorkspaceShell } from './FinanceWorkspaceShell';
import { WorkspaceGrid } from '@/components/grid';
import { EntityEditForm } from '@/components/entity/EntityEditForm';
import { AssistPanelHost, RightDockDetailSheet, RowEditableSublist } from '@/components/major-list';
import { DocumentCopySheet, type DocumentCopyResult, type DocumentCopySourceDocument } from '@/components/document-copy/DocumentCopySheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type EntryTypeFilter = 'all' | 'AR' | 'AP' | 'CN' | 'DN' | 'JE';
type DockMode = 'create' | 'view' | 'edit';
type AssistTab = 'controls' | 'costs' | 'payments';

interface QuickAction {
  label: string;
  to: string;
  variant?: 'default' | 'outline' | 'secondary';
}

interface LedgerPrecheckIssue {
  code: string;
  severity: 'error' | 'warning';
  title: string;
  message: string;
  suggestion?: string;
}

interface LedgerDuplicateCandidate {
  id: string;
  entryNumber: string;
  entryType: string;
  status: string;
  totalAmount: number;
  currency: string;
  partyName: string;
  createdAt?: string;
  jobId?: string;
  matchReasons: string[];
}

interface LedgerPrecheckResult {
  canPost: boolean;
  errors: LedgerPrecheckIssue[];
  warnings: LedgerPrecheckIssue[];
  duplicateCandidates: LedgerDuplicateCandidate[];
  checks: {
    isDraft: boolean;
    hasLines: boolean;
    partyLinked: boolean;
    dueDatePresent: boolean;
    balanced: boolean;
    canAutoBalance: boolean;
    requiresReview: boolean;
    periodStatus: string | null;
  };
}

export interface LedgerWorkspaceConfig {
  title: string;
  subtitle: string;
  Icon: LucideIcon;
  iconClassName?: string;
  moduleBadge?: string;
  domainBadge?: string;
  agentTitle: string;
  agentSummary: string;
  checklistTitle: string;
  checklistStatus: string;
  checklistNote: string;
  checklistItems: string[];
  quickActions: QuickAction[];
  listTemplateSlug: string;
  entryFormTemplateSlug: string;
  lineListTemplateSlug: string;
  lineFormTemplateSlug: string;
  defaultColumnSetId: string;
  amountKpiLabel: string;
  postedKpiLabel: string;
  documentLabel: string;
  duplicateLabel: string;
  createButtonLabel: string;
  entryTypeOptions: Array<{ value: Exclude<EntryTypeFilter, 'all'>; label: string }>;
  lockedEntryType?: Exclude<EntryTypeFilter, 'all'>;
  hiddenHeaderFields?: string[];
  editableHeaderFields: string[];
  inlineHeaderEditableFields: string[];
  inlineLineEditableFields: string[];
  showCostPanel?: boolean;
  showPaymentsPanel?: boolean;
  allowPrint?: boolean;
  createInitialValues?: (entryType: Exclude<EntryTypeFilter, 'all'>) => Record<string, unknown>;
  copyInitialValues?: (record: Record<string, unknown>) => Record<string, unknown>;
  buildEntryCreatePayload: (payload: Record<string, unknown>) => Record<string, unknown>;
  buildHeaderPatchPayload: (payload: Record<string, unknown>) => Record<string, unknown>;
  buildLinePayload: (payload: Record<string, unknown>) => Record<string, unknown>;
}

function firstValue(record: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function asString(record: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  const value = firstValue(record, ...keys);
  return value == null ? '' : String(value);
}

function asNumber(record: Record<string, unknown> | null | undefined, ...keys: string[]): number {
  const value = firstValue(record, ...keys);
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function asNullableString(value: unknown): string | undefined {
  if (value == null) return undefined;
  const next = String(value).trim();
  return next ? next : undefined;
}

function fmtAmount(value: number, currency = 'SGD'): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function buildVarianceLabel(current: number, source: number, label: string): string | null {
  const diff = round2(current - source);
  if (Math.abs(diff) < 0.005) return null;
  const direction = diff > 0 ? 'higher' : 'lower';
  return `${label} ${direction} by ${Math.abs(diff).toFixed(2)}`;
}

function normalizeLine(raw: Record<string, unknown>): Record<string, unknown> {
  const metadata = parseJsonObject(firstValue(raw, 'data', 'metadata'));
  const copiedSourceSnapshot = parseJsonObject(metadata.copied_source_snapshot);
  return {
    ...raw,
    entryId: firstValue(raw, 'entryId', 'entry_id'),
    lineNo: firstValue(raw, 'lineNo', 'line_no'),
    accountId: firstValue(raw, 'accountId', 'account_id'),
    chargeCodeId: firstValue(raw, 'chargeCodeId', 'charge_code_id'),
    unitPrice: firstValue(raw, 'unitPrice', 'unit_price'),
    taxCodeId: firstValue(raw, 'taxCodeId', 'tax_code_id'),
    taxRate: firstValue(raw, 'taxRate', 'tax_rate'),
    taxAmount: firstValue(raw, 'taxAmount', 'tax_amount'),
    createdAt: firstValue(raw, 'createdAt', 'created_at'),
    metadata,
    copiedSourceSnapshot,
    copiedFromSourceLineId: firstValue(metadata, 'copied_from_source_line_id'),
    copiedFromSourceType: firstValue(metadata, 'copied_from_source_type'),
    copiedFromDocumentNumber: firstValue(metadata, 'copied_from_source_document_number', 'copied_from_document_number'),
  };
}

type CopiedLineComparison = {
  sourceLineId: string | null;
  targetLineId: string;
  sourceDescription: string;
  targetDescription: string;
  sourceAmount: number;
  targetAmount: number;
  sourceUnitPrice: number;
  targetUnitPrice: number;
  sourceQuantity: number;
  targetQuantity: number;
  sourceTaxRate: number;
  targetTaxRate: number;
  sourceTaxAmount: number;
  targetTaxAmount: number;
  warnings: string[];
};

function normalizeEntryDetail(raw: Record<string, unknown>): Record<string, unknown> {
  const lines = Array.isArray(raw.lines)
    ? raw.lines.map((line) => normalizeLine(line as Record<string, unknown>))
    : [];
  return {
    ...raw,
    entryNumber: firstValue(raw, 'entryNumber', 'entry_number'),
    entryType: firstValue(raw, 'entryType', 'entry_type'),
    customerId: firstValue(raw, 'customerId', 'customer_id'),
    vendorId: firstValue(raw, 'vendorId', 'vendor_id'),
    jobId: firstValue(raw, 'jobId', 'job_id'),
    entryDate: firstValue(raw, 'entryDate', 'entry_date'),
    dueDate: firstValue(raw, 'dueDate', 'due_date'),
    exchangeRate: firstValue(raw, 'exchangeRate', 'exchange_rate'),
    totalAmount: firstValue(raw, 'totalAmount', 'total_amount'),
    baseAmount: firstValue(raw, 'baseAmount', 'base_amount'),
    paidAmount: firstValue(raw, 'paidAmount', 'paid_amount'),
    postedAt: firstValue(raw, 'postedAt', 'posted_at'),
    narration: firstValue(raw, 'narration'),
    lines,
  };
}

function buildPrintUrl(entryId: string): string {
  const token = getToken();
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${BASE}/ledger-entries/${entryId}/print${qs}`;
}

function ControlCheck({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-xl border bg-background px-3 py-2 text-sm">
      <span className="text-slate-600">{label}</span>
      <Badge variant="outline" className={ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-600'}>
        {ok ? 'Ready' : 'Needs attention'}
      </Badge>
    </div>
  );
}

function IssueList({ title, issues, tone }: { title: string; issues: LedgerPrecheckIssue[]; tone: 'error' | 'warning' }) {
  if (issues.length === 0) return null;
  return (
    <Card className={tone === 'error' ? 'border-red-200 bg-red-50/60' : 'border-amber-200 bg-amber-50/60'}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <AlertTriangle className={tone === 'error' ? 'h-4 w-4 text-red-600' : 'h-4 w-4 text-amber-600'} />
          {title}
        </div>
        <div className="space-y-2 text-sm">
          {issues.map((issue) => (
            <div key={issue.code} className="rounded-xl border bg-white/90 px-3 py-2">
              <div className="font-medium text-slate-900">{issue.title}</div>
              <div className="text-slate-600">{issue.message}</div>
              {issue.suggestion && (
                <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs text-blue-800">
                  <span className="mt-px shrink-0 text-blue-500">&#128161;</span>
                  <span>{issue.suggestion}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function LedgerWorkspacePage({ config }: { config: LedgerWorkspaceConfig }) {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const initialTypeFilter = config.lockedEntryType ?? (config.entryTypeOptions[0]?.value ?? 'AR');
  const [entryTypeFilter, setEntryTypeFilter] = useState<EntryTypeFilter>(initialTypeFilter);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('updatedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [columnSetId, setColumnSetId] = useState(config.defaultColumnSetId);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [selectedEntrySummary, setSelectedEntrySummary] = useState<Record<string, unknown> | null>(null);
  const [entryDetail, setEntryDetail] = useState<Record<string, unknown> | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [precheck, setPrecheck] = useState<LedgerPrecheckResult | null>(null);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [dockMode, setDockMode] = useState<DockMode>('view');
  const [dockOpen, setDockOpen] = useState(false);
  const [assistOpen, setAssistOpen] = useState(true);
  const [assistTab, setAssistTab] = useState<AssistTab>('controls');
  const [lineMeta, setLineMeta] = useState<EntityMeta | null>(null);
  const [lineEditorOpen, setLineEditorOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<Record<string, unknown> | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [createSeed, setCreateSeed] = useState<Record<string, unknown> | null>(null);
  const [copyLinesOpen, setCopyLinesOpen] = useState(false);
  const [duplicateSaving, setDuplicateSaving] = useState(false);
  const [recentCopyHighlight, setRecentCopyHighlight] = useState<{ entryId: string; lineIds: string[] } | null>(null);
  const [recentCopyContext, setRecentCopyContext] = useState<{ entryId: string; copyResult: DocumentCopyResult; sourceDetail: DocumentCopySourceDocument | null } | null>(null);

  const entryFilters = useMemo(() => {
    const next: Record<string, string> = {};
    const effectiveEntryType = config.lockedEntryType ?? entryTypeFilter;
    if (effectiveEntryType !== 'all') next.entryType = effectiveEntryType;
    if (statusFilter !== 'all') next.status = statusFilter;
    return next;
  }, [config.lockedEntryType, entryTypeFilter, statusFilter]);

  const entryData = useEntityData({
    slug: 'ledger-entries',
    templateSlug: config.listTemplateSlug,
    viewType: 'list',
    search: search || undefined,
    sort,
    order,
    filters: entryFilters,
    limit: 20,
  });

  const { template: lineTemplate } = useViewTemplate(config.lineListTemplateSlug, 'list', lineMeta);

  const assistTabs = useMemo(() => {
    const tabs: AssistTab[] = ['controls'];
    if (config.showCostPanel) tabs.push('costs');
    if (config.showPaymentsPanel !== false) tabs.push('payments');
    return tabs;
  }, [config.showCostPanel, config.showPaymentsPanel]);

  useEffect(() => {
    if (!assistTabs.includes(assistTab)) setAssistTab('controls');
  }, [assistTab, assistTabs]);

  useEffect(() => {
    let cancelled = false;
    api.get<EntityMeta>('/entities/ledger-lines')
      .then((meta) => {
        if (!cancelled) setLineMeta(meta);
      })
      .catch(() => {
        if (!cancelled) setLineMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const presets = entryData.template?.list?.columnSets ?? [];
    if (presets.length === 0) return;
    if (!presets.some((preset) => preset.id === columnSetId)) {
      setColumnSetId(presets[0]?.id ?? config.defaultColumnSetId);
    }
  }, [columnSetId, config.defaultColumnSetId, entryData.template?.list?.columnSets]);

  const loadPrecheck = useCallback(async (entryId: string): Promise<LedgerPrecheckResult | null> => {
    setPrecheckLoading(true);
    try {
      const result = await api.get<LedgerPrecheckResult>(`/ledger-entries/${entryId}/precheck`);
      setPrecheck(result);
      return result;
    } catch {
      setPrecheck(null);
      return null;
    } finally {
      setPrecheckLoading(false);
    }
  }, []);

  const loadEntryDetail = useCallback(async (entryId: string) => {
    setDetailLoading(true);
    try {
      const [record] = await Promise.all([
        api.get<Record<string, unknown>>(`/ledger-entries/${entryId}`),
        loadPrecheck(entryId),
      ]);
      setEntryDetail(normalizeEntryDetail(record));
      setSelectedEntryId(entryId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to load ${config.documentLabel.toLowerCase()} detail`);
    } finally {
      setDetailLoading(false);
    }
  }, [config.documentLabel, loadPrecheck, toast]);

  useEffect(() => {
    const requestedEntryId = searchParams.get('entryId');
    if (!requestedEntryId) return;
    if (requestedEntryId === selectedEntryId) return;
    setDockMode('view');
    setDockOpen(true);
    setAssistOpen(true);
    setSelectedEntrySummary({ id: requestedEntryId });
    void loadEntryDetail(requestedEntryId);
  }, [loadEntryDetail, searchParams, selectedEntryId]);

  const refreshAll = useCallback(async (entryId?: string | null) => {
    entryData.refresh();
    if (entryId) {
      await loadEntryDetail(entryId);
      return;
    }
    setPrecheck(null);
  }, [entryData, loadEntryDetail]);

  const openCreateDock = useCallback((seed?: Record<string, unknown> | null) => {
    setDockMode('create');
    setDockOpen(true);
    setEntryDetail(null);
    setPrecheck(null);
    setSelectedEntryId(null);
    setSelectedEntrySummary(null);
    setSelectedLineId(null);
    setEditingLine(null);
    setLineEditorOpen(false);
    setCreateSeed(seed ?? null);
  }, []);

  const openEntryDock = useCallback((row: Record<string, unknown>, mode: DockMode = 'view') => {
    const entryId = asString(row, 'id');
    if (!entryId) return;
    setDockMode(mode);
    setDockOpen(true);
    setAssistOpen(true);
    setSelectedLineId(null);
    setEditingLine(null);
    setLineEditorOpen(false);
    setCreateSeed(null);
    setSelectedEntrySummary(row);
    void loadEntryDetail(entryId);
  }, [loadEntryDetail]);

  const activeEntry = entryDetail;
  const activeEntryId = selectedEntryId;
  const displayEntry = useMemo(
    () => (activeEntry ? { ...(selectedEntrySummary ?? {}), ...activeEntry } : selectedEntrySummary),
    [activeEntry, selectedEntrySummary],
  );

  const entryLines = useMemo(
    () => (Array.isArray(activeEntry?.lines) ? (activeEntry.lines as Record<string, unknown>[]) : []),
    [activeEntry],
  );
  const selectedLine = useMemo(
    () => entryLines.find((line) => asString(line, 'id') === selectedLineId) ?? null,
    [entryLines, selectedLineId],
  );

  const copiedLineComparisons = useMemo<CopiedLineComparison[]>(() => {
    if (!recentCopyContext || recentCopyContext.entryId !== activeEntryId) return [];
    const sourceLines = new Map((recentCopyContext.sourceDetail?.lines ?? []).map((line) => [String(line.sourceLineId ?? ''), line]));
    return recentCopyContext.copyResult.lineMappings.map((mapping) => {
      const target = entryLines.find((line) => asString(line, 'id') === mapping.targetLineId);
      const source = sourceLines.get(String(mapping.sourceLineId ?? '')) ?? null;
      const snapshot = target ? parseJsonObject(target.copiedSourceSnapshot) : {};
      const sourceDescription = String(source?.description ?? snapshot.description ?? '');
      const sourceAmount = Number(source?.amount ?? snapshot.amount ?? 0);
      const sourceUnitPrice = Number(source?.unitPrice ?? snapshot.unitPrice ?? 0);
      const sourceQuantity = Number(source?.quantity ?? snapshot.quantity ?? 0);
      const sourceTaxRate = Number(source?.taxRate ?? snapshot.taxRate ?? 0);
      const sourceTaxAmount = Number(source?.taxAmount ?? snapshot.taxAmount ?? 0);
      const targetAmount = asNumber(target, 'amount');
      const targetUnitPrice = asNumber(target, 'unitPrice');
      const targetQuantity = asNumber(target, 'quantity');
      const targetTaxRate = asNumber(target, 'taxRate');
      const targetTaxAmount = asNumber(target, 'taxAmount');
      const warnings = [
        buildVarianceLabel(targetUnitPrice, sourceUnitPrice, 'Unit price'),
        buildVarianceLabel(targetQuantity, sourceQuantity, 'Quantity'),
        buildVarianceLabel(targetAmount, sourceAmount, 'Line amount'),
        buildVarianceLabel(targetTaxRate, sourceTaxRate, 'Tax rate'),
        buildVarianceLabel(targetTaxAmount, sourceTaxAmount, 'Tax amount'),
      ].filter((warning): warning is string => Boolean(warning));
      return {
        sourceLineId: mapping.sourceLineId,
        targetLineId: mapping.targetLineId,
        sourceDescription,
        targetDescription: asString(target, 'description'),
        sourceAmount,
        targetAmount,
        sourceUnitPrice,
        targetUnitPrice,
        sourceQuantity,
        targetQuantity,
        sourceTaxRate,
        targetTaxRate,
        sourceTaxAmount,
        targetTaxAmount,
        warnings,
      };
    });
  }, [activeEntryId, entryLines, recentCopyContext]);

  const selectedCopiedLineComparison = useMemo(() => {
    if (copiedLineComparisons.length === 0) return null;
    return copiedLineComparisons.find((item) => item.targetLineId === selectedLineId) ?? copiedLineComparisons[0] ?? null;
  }, [copiedLineComparisons, selectedLineId]);

  const visibleEntryColumns = useMemo(() => {
    const columns = entryData.template?.columns ?? [];
    const preset = entryData.template?.list?.columnSets?.find((item) => item.id === columnSetId);
    if (!preset?.visibleColumns?.length) return columns;
    const visible = new Set(preset.visibleColumns);
    return columns.filter((column) => visible.has(column.field));
  }, [columnSetId, entryData.template?.columns, entryData.template?.list?.columnSets]);

  const lineColumns = useMemo(() => lineTemplate?.columns ?? [], [lineTemplate?.columns]);

  // Suspense-amount badge column — injected before DSL columns
  type DataRow = Record<string, unknown>;
  const suspenseColumn = useMemo<ColumnDef<DataRow>[]>(() => [{
    id: '_suspense',
    size: 148,
    minSize: 100,
    maxSize: 200,
    enableResizing: false,
    enableSorting: false,
    header: () => null,
    cell: ({ row }) => {
      const amt = Number((row.original as DataRow).suspense_amount ?? 0);
      if (amt === 0) return null;
      return (
        <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs gap-1 whitespace-nowrap">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unallocated
        </Badge>
      );
    },
  }], []);


  const kpis = useMemo(() => {
    return entryData.rows.reduce<{ documents: number; drafts: number; posted: number; openBalance: number }>(
      (acc, row) => {
        acc.documents += 1;
        const status = asString(row, 'status').toLowerCase();
        const balance = asNumber(row, 'balance_amount', 'balanceAmount', 'totalAmount');
        if (status === 'draft') acc.drafts += 1;
        if (status === 'posted' || status === 'paid') acc.posted += 1;
        acc.openBalance += balance;
        return acc;
      },
      { documents: 0, drafts: 0, posted: 0, openBalance: 0 },
    );
  }, [entryData.rows]);

  const fallbackChecks = useMemo(() => ({
    isDraft: asString(activeEntry, 'status') === 'draft',
    hasLines: entryLines.length > 0,
    partyLinked: Boolean(asString(activeEntry, 'customerId', 'vendorId')) || config.lockedEntryType === 'JE',
    dueDatePresent: Boolean(asString(activeEntry, 'dueDate')) || config.lockedEntryType === 'JE',
    balanced: true,
    canAutoBalance: false,
    requiresReview: false,
    periodStatus: null as string | null,
  }), [activeEntry, config.lockedEntryType, entryLines.length]);

  const postingChecks = precheck?.checks ?? fallbackChecks;
  const activeEntryPolicy = useMemo(() => getLedgerDocumentPolicy(asString(displayEntry, 'status') || 'draft'), [displayEntry]);
  const canPost = Boolean(activeEntryId) && activeEntryPolicy.canPost && postingChecks.isDraft && (precheck ? precheck.canPost : postingChecks.hasLines);

  const createEntryType = (config.lockedEntryType ?? (entryTypeFilter === 'all' ? config.entryTypeOptions[0]?.value : entryTypeFilter) ?? 'AR') as Exclude<EntryTypeFilter, 'all'>;
  const createInitialValues = useMemo(() => ({
    ...(config.createInitialValues?.(createEntryType) ?? { entryType: createEntryType, currency: 'SGD', exchangeRate: 1 }),
    ...(createSeed ?? {}),
  }), [config, createEntryType, createSeed]);

  const handleEntryCellEdit = useCallback(async (rowId: string, field: string, value: unknown) => {
    if (!config.inlineHeaderEditableFields.includes(field)) {
      throw new Error(`Inline edit is locked for ${field}. Use the ${config.documentLabel.toLowerCase()} dock.`);
    }
    await api.patch(`/ledger-entries/${rowId}`, { [field]: value || null });
    await refreshAll(activeEntryId === rowId ? rowId : null);
  }, [activeEntryId, config.documentLabel, config.inlineHeaderEditableFields, refreshAll]);

  const handleLineCellEdit = useCallback(async (rowId: string, field: string, value: unknown) => {
    if (!activeEntryId) throw new Error(`Open a ${config.documentLabel.toLowerCase()} before editing its lines`);
    if (!activeEntryPolicy.canEditLine) {
      throw new Error(`${config.documentLabel} lines can only be edited while the document is in draft status.`);
    }
    if (!config.inlineLineEditableFields.includes(field)) {
      throw new Error(`This line field is locked for inline edit. Use Edit Line for ${config.documentLabel.toLowerCase()} mapping changes.`);
    }
    const normalizedValue = ['quantity', 'unitPrice', 'taxRate', 'debit', 'credit'].includes(field)
      ? Number(value ?? 0)
      : value;
    await api.patch(`/ledger-entries/${activeEntryId}/lines/${rowId}`, { [field]: normalizedValue });
    await refreshAll(activeEntryId);
  }, [activeEntryId, activeEntryPolicy.canEditLine, config.documentLabel, config.inlineLineEditableFields, refreshAll]);

  const handleDeleteLine = useCallback(async () => {
    if (!activeEntryId || !selectedLine) return;
    const confirmed = window.confirm(`Delete line ${asString(selectedLine, 'lineNo')} from this draft ${config.documentLabel.toLowerCase()}?`);
    if (!confirmed) return;
    try {
      await api.delete(`/ledger-entries/${activeEntryId}/lines/${asString(selectedLine, 'id')}`);
      setSelectedLineId(null);
      setEditingLine(null);
      setLineEditorOpen(false);
      await refreshAll(activeEntryId);
      toast.success(`${config.documentLabel} line deleted`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to delete ${config.documentLabel.toLowerCase()} line`);
    }
  }, [activeEntryId, config.documentLabel, refreshAll, selectedLine, toast]);

  const openLineEditor = useCallback((line?: Record<string, unknown> | null) => {
    setEditingLine(line ?? null);
    setSelectedLineId(line ? asString(line, 'id') : null);
    setLineEditorOpen(true);
  }, []);

  const closeLineEditor = useCallback(() => {
    setEditingLine(null);
    setLineEditorOpen(false);
  }, []);

  const handlePostDraft = useCallback(async () => {
    if (!activeEntryId) return;
    const latestPrecheck = await loadPrecheck(activeEntryId);
    if (latestPrecheck && !latestPrecheck.canPost) {
      toast.error(latestPrecheck.errors[0]?.message ?? `${config.documentLabel} precheck failed`);
      return;
    }
    try {
      await api.post(`/ledger-entries/${activeEntryId}/post`, {});
      await refreshAll(activeEntryId);
      toast.success(`Draft ${config.documentLabel.toLowerCase()} posted`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to post draft ${config.documentLabel.toLowerCase()}`);
    }
  }, [activeEntryId, config.documentLabel, loadPrecheck, refreshAll, toast]);

  const handleVoidEntry = useCallback(async () => {
    if (!activeEntryId) return;
    const confirmed = window.confirm(`Void this ${config.documentLabel.toLowerCase()}? This should only be used for wrong draft or controlled cancellation.`);
    if (!confirmed) return;
    try {
      await api.post(`/ledger-entries/${activeEntryId}/void`, { reason: `Voided from ${config.title}` });
      await refreshAll(activeEntryId);
      toast.success(`${config.documentLabel} voided`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to void ${config.documentLabel.toLowerCase()}`);
    }
  }, [activeEntryId, config.documentLabel, config.title, refreshAll, toast]);

  const handlePrint = useCallback(() => {
    if (!activeEntryId) return;
    window.open(buildPrintUrl(activeEntryId), '_blank', 'noopener,noreferrer');
  }, [activeEntryId]);

  const handleDuplicateEntry = useCallback(async () => {
    if (!activeEntryId) return;
    setDuplicateSaving(true);
    try {
      const result = await api.post<{ mode: string; sourceType: string; copyResult?: DocumentCopyResult; entry: Record<string, unknown> }>(`/ledger-entries/${activeEntryId}/duplicate`, {});
      const duplicatedId = asString(result.entry, 'id');
      setDockMode('view');
      if (duplicatedId) {
        setRecentCopyHighlight({
          entryId: duplicatedId,
          lineIds: result.copyResult?.copiedLineIds ?? [],
        });
        setSelectedLineId(result.copyResult?.copiedLineIds?.[0] ?? null);
        await refreshAll(duplicatedId);
      } else {
        setRecentCopyHighlight({
          entryId: activeEntryId,
          lineIds: result.copyResult?.copiedLineIds ?? [],
        });
        setSelectedLineId(result.copyResult?.copiedLineIds?.[0] ?? null);
        await refreshAll(activeEntryId);
      }
      toast.success(`Draft ${config.documentLabel.toLowerCase()} duplicated`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to duplicate ${config.documentLabel.toLowerCase()}`);
    } finally {
      setDuplicateSaving(false);
    }
  }, [activeEntryId, config.documentLabel, refreshAll, toast]);

  const handleApplyView = useCallback((view: { sort?: string; order?: 'asc' | 'desc'; filters?: Record<string, string> }) => {
    setSort(view.sort ?? 'updatedAt');
    setOrder(view.order ?? 'desc');
    if (!config.lockedEntryType) {
      const nextType = (view.filters?.entryType as EntryTypeFilter | undefined) ?? 'all';
      setEntryTypeFilter(nextType);
    }
    setStatusFilter(view.filters?.status ?? 'all');
  }, [config.lockedEntryType]);

  const currentViewConfig = useMemo(() => ({
    sort,
    order,
    filters: {
      entryType: config.lockedEntryType ?? (entryTypeFilter === 'all' ? '' : entryTypeFilter),
      status: statusFilter === 'all' ? '' : statusFilter,
    },
  }), [config.lockedEntryType, entryTypeFilter, order, sort, statusFilter]);

  useEffect(() => {
    if (!recentCopyHighlight || recentCopyHighlight.entryId !== activeEntryId || recentCopyHighlight.lineIds.length === 0) return;
    const firstLineId = recentCopyHighlight.lineIds[0];
    const timer = window.setTimeout(() => {
      const targetRow = document.querySelector(`[data-row-id="${firstLineId}"]`);
      if (targetRow instanceof HTMLElement) {
        targetRow.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [activeEntryId, recentCopyHighlight, entryLines]);

  return (
    <FinanceWorkspaceShell
      title={config.title}
      subtitle={config.subtitle}
      Icon={config.Icon}
      iconClassName={config.iconClassName}
      moduleBadge={config.moduleBadge}
      domainBadge={config.domainBadge}
      agentTitle={config.agentTitle}
      agentSummary={config.agentSummary}
      checklistTitle={config.checklistTitle}
      checklistStatus={config.checklistStatus}
      checklistNote={config.checklistNote}
      checklistItems={config.checklistItems}
      quickActions={config.quickActions}
      headerActions={(
        <>
          <Button variant="outline" size="sm" onClick={() => setAssistOpen((open) => !open)}>
            {assistOpen ? <PanelRightClose className="mr-2 h-4 w-4" /> : <PanelRightOpen className="mr-2 h-4 w-4" />}
            Assist
          </Button>
          <Button variant="outline" size="sm" onClick={() => refreshAll(activeEntryId)} disabled={entryData.loading || detailLoading || precheckLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${entryData.loading || detailLoading || precheckLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => openCreateDock()}>
            <FilePlus2 className="mr-2 h-4 w-4" />
            {config.createButtonLabel}
          </Button>
        </>
      )}
    >
      <div className="grid gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Documents</div><div className="mt-1 text-2xl font-semibold">{kpis.documents}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Drafts</div><div className="mt-1 text-2xl font-semibold text-amber-700">{kpis.drafts}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">{config.postedKpiLabel}</div><div className="mt-1 text-2xl font-semibold text-emerald-700">{kpis.posted}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">{config.amountKpiLabel}</div><div className="mt-1 text-2xl font-semibold text-sky-700">{fmtAmount(kpis.openBalance)}</div></CardContent></Card>
      </div>

      <div className="space-y-3 rounded-2xl border bg-background p-4">
        {!config.lockedEntryType && config.entryTypeOptions.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {config.entryTypeOptions.map((option) => (
              <Button
                key={option.value}
                variant={entryTypeFilter === option.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setEntryTypeFilter(option.value);
                  entryData.setPage(1);
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={entryData.template?.page?.searchPlaceholder ?? `Search ${config.documentLabel.toLowerCase()} / party / job`}
            className="min-w-[280px] max-w-xl"
          />
          <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); entryData.setPage(1); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {LEDGER_STATUS_FILTER_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            {(entryData.template?.list?.columnSets ?? []).map((preset) => (
              <Button key={preset.id} variant={columnSetId === preset.id ? 'default' : 'outline'} size="sm" onClick={() => setColumnSetId(preset.id)}>
                {preset.label}
              </Button>
            ))}
          </div>
        </div>

        {entryData.meta ? (
          <WorkspaceGrid
            slug="ledger-entries"
            meta={entryData.meta}
            columns={visibleEntryColumns}
            data={entryData.rows}
            total={entryData.total}
            page={entryData.page}
            pageSize={20}
            onPageChange={entryData.setPage}
            onSort={(field, nextOrder) => {
              setSort(field);
              setOrder(nextOrder);
            }}
            sortField={sort}
            sortOrder={order}
            loading={entryData.loading}
            onRefresh={() => refreshAll(activeEntryId)}
            onRowClick={(row) => openEntryDock(row, 'view')}
            onQuickEdit={(row) => openEntryDock(row, 'edit')}
            showSavedViews
            currentViewConfig={currentViewConfig}
            onApplyView={handleApplyView}
            onCellEditOverride={config.inlineHeaderEditableFields.length > 0 ? handleEntryCellEdit : undefined}
            prependColumns={suspenseColumn}
            toolbarExtra={<div className="text-xs text-muted-foreground">Row click opens the {config.documentLabel.toLowerCase()} dock. Inline edit is limited to approved header fields only.</div>}
          />
        ) : (
          <Card><CardContent className="p-8 text-sm text-muted-foreground">Loading {config.documentLabel.toLowerCase()} workspace metadata…</CardContent></Card>
        )}
      </div>

      <AssistPanelHost open={assistOpen} onOpenChange={setAssistOpen} title={`${config.documentLabel} Assist`} description="Posting readiness, duplicate detection, and related context without leaving the master list" defaultWidth={460}>
        {!displayEntry ? (
          <div className="p-4 text-sm text-muted-foreground">Select a {config.documentLabel.toLowerCase()} row to inspect readiness, related context, and payment state.</div>
        ) : (
          <div className="flex h-full min-h-0 flex-col gap-4 p-3">
            <Card className="border-sky-200 bg-sky-50/60">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-[0.18em] text-sky-700">Selected {config.documentLabel}</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{asString(displayEntry, 'entryNumber')}</div>
                    <div className="text-sm text-slate-600">{asString(displayEntry, 'customer_name', 'vendor_name') || (config.lockedEntryType === 'JE' ? 'General ledger journal' : 'No party linked yet')}</div>
                  </div>
                  <Badge variant="outline" className={activeEntryPolicy.badgeClassName}>{activeEntryPolicy.label}</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Amount</div><div className="text-base font-semibold">{fmtAmount(asNumber(displayEntry, 'totalAmount'), asString(displayEntry, 'currency') || 'SGD')}</div></div>
                  <div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Lines</div><div className="text-base font-semibold">{entryLines.length}</div></div>
                </div>
              </CardContent>
            </Card>

            {assistTabs.length > 1 ? (
              <Tabs value={assistTab} onValueChange={(value) => setAssistTab(value as AssistTab)} className="flex min-h-0 flex-1 flex-col">
                <TabsList className={`grid ${assistTabs.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <TabsTrigger value="controls">Controls</TabsTrigger>
                  {config.showCostPanel ? <TabsTrigger value="costs">Unbilled Cost</TabsTrigger> : null}
                  {config.showPaymentsPanel !== false ? <TabsTrigger value="payments">Payments</TabsTrigger> : null}
                </TabsList>
                <TabsContent value="controls" className="mt-3 space-y-3 overflow-auto">
                  <ControlCheck label="Still draft" ok={postingChecks.isDraft} />
                  <ControlCheck label="At least one line" ok={postingChecks.hasLines} />
                  <ControlCheck label={config.lockedEntryType === 'JE' ? 'Journal account coverage' : 'Party linked'} ok={postingChecks.partyLinked} />
                  <ControlCheck label={config.lockedEntryType === 'JE' ? 'Balanced entry' : 'Due date captured'} ok={config.lockedEntryType === 'JE' ? postingChecks.balanced : postingChecks.dueDatePresent} />
                  {config.lockedEntryType !== 'JE' ? <ControlCheck label="Balanced after control line" ok={postingChecks.balanced} /> : null}
                  {postingChecks.canAutoBalance ? (
                    <Card className="border-emerald-200 bg-emerald-50/60"><CardContent className="p-4 text-sm text-emerald-800">Control-account auto-balance is available for this draft. Posting will generate the subledger counterpart line automatically.</CardContent></Card>
                  ) : null}
                  {postingChecks.periodStatus ? (
                    <Card><CardContent className="p-4 text-sm text-slate-600">Period status: <span className="font-semibold text-slate-900">{postingChecks.periodStatus}</span></CardContent></Card>
                  ) : null}
                  <IssueList title="Posting blockers" issues={precheck?.errors ?? []} tone="error" />
                  <IssueList title="Needs review" issues={precheck?.warnings ?? []} tone="warning" />
                  {precheck?.duplicateCandidates?.length ? (
                    <Card className="border-amber-200 bg-amber-50/60">
                      <CardContent className="space-y-3 p-4">
                        <div className="text-sm font-semibold text-slate-900">{config.duplicateLabel}</div>
                        <div className="space-y-2 text-sm">
                          {precheck.duplicateCandidates.map((candidate) => (
                            <div key={candidate.id} className="rounded-xl border bg-white/90 px-3 py-2">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-medium text-slate-900">{candidate.entryNumber}</div>
                                  <div className="text-slate-600">{candidate.partyName} · {fmtAmount(candidate.totalAmount, candidate.currency)}</div>
                                  <div className="text-xs text-muted-foreground">{candidate.matchReasons.join(' · ')}</div>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => openEntryDock({ id: candidate.id }, 'view')}>View</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </TabsContent>
                {config.showCostPanel ? (
                  <TabsContent value="costs" className="mt-3 min-h-0 overflow-auto">
                    {asString(displayEntry, 'jobId') ? (
                      <RowEditableSublist slug="job-cost-lines" title="Unbilled Cost Lines" filters={{ jobId: asString(displayEntry, 'jobId'), billingStatus: 'unbilled' }} pageSize={20} emptyText="No unbilled cost lines for this job" editable={false} />
                    ) : (
                      <Card><CardContent className="p-4 text-sm text-muted-foreground">Link the document to a job to inspect unbilled cost lines here.</CardContent></Card>
                    )}
                  </TabsContent>
                ) : null}
                {config.showPaymentsPanel !== false ? (
                  <TabsContent value="payments" className="mt-3 min-h-0 overflow-auto">
                    {activeEntryId ? (
                      <RowEditableSublist slug="payments" title="Related Payments" filters={{ entryId: activeEntryId }} pageSize={20} emptyText="No payments recorded for this entry" editable={false} />
                    ) : null}
                  </TabsContent>
                ) : null}
              </Tabs>
            ) : (
              <div className="space-y-3 overflow-auto">
                <ControlCheck label="Still draft" ok={postingChecks.isDraft} />
                <ControlCheck label="At least one line" ok={postingChecks.hasLines} />
                <ControlCheck label={config.lockedEntryType === 'JE' ? 'Journal account coverage' : 'Party linked'} ok={postingChecks.partyLinked} />
                <ControlCheck label={config.lockedEntryType === 'JE' ? 'Balanced entry' : 'Due date captured'} ok={config.lockedEntryType === 'JE' ? postingChecks.balanced : postingChecks.dueDatePresent} />
                <IssueList title="Posting blockers" issues={precheck?.errors ?? []} tone="error" />
                <IssueList title="Needs review" issues={precheck?.warnings ?? []} tone="warning" />
              </div>
            )}

            <Card>
              <CardContent className="space-y-2 p-4 text-sm text-slate-600">
                <div className="flex items-center gap-2 text-slate-900"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Posting guardrail</div>
                <p>Header edits stay narrow after creation. Amount logic lives in detail lines so total drift is always recalculated from source rows.</p>
                {asString(displayEntry, 'jobId') ? (
                  <p>
                    Related job:{' '}
                    <Link className="text-sky-700 hover:underline" to={`/e/jobs/${asString(displayEntry, 'jobId')}`}>
                      {asString(displayEntry, 'job_number', 'jobId')}
                    </Link>
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </AssistPanelHost>

      <RightDockDetailSheet
        open={dockOpen}
        onOpenChange={(open) => {
          setDockOpen(open);
          if (!open) {
            setDockMode('view');
            setEditingLine(null);
            setLineEditorOpen(false);
            setSelectedLineId(null);
          }
        }}
        width="xl"
        title={dockMode === 'create' ? config.createButtonLabel : asString(displayEntry, 'entryNumber') || config.documentLabel}
        description={dockMode === 'create' ? `Use DSL-guided fields to create a new draft ${config.documentLabel.toLowerCase()} before adding detail lines.` : `Header control and detail lines stay in one dock for safer ${config.documentLabel.toLowerCase()} operations.`}
        summary={dockMode === 'create' ? (
          <Card className="border-amber-200 bg-amber-50/60"><CardContent className="space-y-2 p-4 text-sm text-slate-600"><div className="flex items-center gap-2 text-slate-900"><CircleAlert className="h-4 w-4 text-amber-600" /> Draft-first workflow</div><p>Create the header first, then add detail lines. Posting only becomes available after the draft passes readiness checks.</p></CardContent></Card>
        ) : displayEntry ? (
          <div className="space-y-4">
            <Card className="border-sky-200 bg-sky-50/60"><CardContent className="space-y-3 p-4"><div className="flex items-start justify-between gap-3"><div><div className="text-xs font-medium uppercase tracking-[0.18em] text-sky-700">{config.documentLabel} Header</div><div className="mt-1 text-lg font-semibold text-slate-900">{asString(displayEntry, 'entryNumber')}</div><div className="text-sm text-slate-600">{asString(displayEntry, 'customer_name', 'vendor_name') || (config.lockedEntryType === 'JE' ? 'General ledger journal' : 'No party linked')}</div></div><div className="flex flex-wrap gap-2"><Badge variant="outline" className="border-sky-200 bg-white text-sky-700">{asString(displayEntry, 'entryType')}</Badge><Badge variant="outline" className={activeEntryPolicy.badgeClassName}>{activeEntryPolicy.label}</Badge></div></div><div className="grid gap-3 md:grid-cols-4"><div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Currency</div><div className="text-sm font-semibold">{asString(displayEntry, 'currency') || 'SGD'}</div></div><div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Amount</div><div className="text-sm font-semibold">{fmtAmount(asNumber(displayEntry, 'totalAmount'), asString(displayEntry, 'currency') || 'SGD')}</div></div><div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Due / Date</div><div className="text-sm font-semibold">{asString(displayEntry, 'dueDate', 'entryDate') || 'Not set'}</div></div><div className="rounded-xl border bg-white px-3 py-2"><div className="text-xs text-muted-foreground">Lines</div><div className="text-sm font-semibold">{entryLines.length}</div></div></div></CardContent></Card>
            {detailLoading ? <div className="text-sm text-muted-foreground">Loading {config.documentLabel.toLowerCase()} detail…</div> : null}
          </div>
        ) : null}
        footer={dockMode === 'create' ? null : activeEntryId ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setDockMode((mode) => mode === 'edit' ? 'view' : 'edit')}><Pencil className="mr-2 h-4 w-4" />{dockMode === 'edit' ? 'View Header' : 'Edit Header'}</Button>
            <Button size="sm" variant="outline" onClick={() => openLineEditor()} disabled={!activeEntryPolicy.canAddLine}><FilePlus2 className="mr-2 h-4 w-4" />Add Line</Button>
            {config.allowPrint ? <Button size="sm" variant="outline" onClick={handlePrint}><Printer className="mr-2 h-4 w-4" />Print</Button> : null}
            <Button size="sm" variant="outline" onClick={() => void handleDuplicateEntry()} disabled={duplicateSaving}>
              {duplicateSaving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BookCopy className="mr-2 h-4 w-4" />}
              Copy Entire
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCopyLinesOpen(true)} disabled={!activeEntryPolicy.canAddLine}>
              <BookCopy className="mr-2 h-4 w-4" />
              Copy Line
            </Button>
            <Button size="sm" onClick={handlePostDraft} disabled={!canPost}><ShieldCheck className="mr-2 h-4 w-4" />Post Draft</Button>
            <Button size="sm" variant="destructive" className="ml-auto" onClick={handleVoidEntry} disabled={!activeEntryPolicy.canVoid}><Trash2 className="mr-2 h-4 w-4" />Void</Button>
          </div>
        ) : null}
      >
        {dockMode === 'create' ? (
          entryData.meta ? (
            <EntityEditForm meta={entryData.meta} slug="ledger-entries" templateSlug={config.entryFormTemplateSlug} record={null} initialValues={createInitialValues} hiddenFieldNames={config.lockedEntryType ? ['entryType', ...(config.hiddenHeaderFields ?? [])] : config.hiddenHeaderFields} stickyActions onSaved={(result) => { const entryId = asString(result, 'id'); setDockMode('view'); setCreateSeed(null); void refreshAll(entryId); toast.success(`Draft ${config.documentLabel.toLowerCase()} created`); }} onCancel={() => setDockOpen(false)} submitOverride={async ({ payload }) => api.post('/ledger-entries', config.buildEntryCreatePayload(payload))} />
          ) : null
        ) : displayEntry && entryData.meta ? (
          <div className="space-y-4">
            <div className="rounded-2xl border bg-background p-4">
              <div className="mb-3 flex items-center justify-between gap-3"><div><div className="text-sm font-semibold">Header Control</div><div className="text-xs text-muted-foreground">Desktop edit uses the 6-column DSL form baseline. Existing documents only expose safe header edits.</div></div><Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{dockMode === 'edit' ? 'Edit mode' : 'View mode'}</Badge></div>
              {dockMode === 'edit' ? (
                <EntityEditForm meta={entryData.meta} slug="ledger-entries" templateSlug={config.entryFormTemplateSlug} record={displayEntry} fieldNames={config.editableHeaderFields} hiddenFieldNames={config.lockedEntryType ? ['entryType', ...(config.hiddenHeaderFields ?? [])] : config.hiddenHeaderFields} stickyActions showTemplateSourceBadge onSaved={() => { void refreshAll(activeEntryId); setDockMode('view'); toast.success(`${config.documentLabel} header updated`); }} onCancel={() => setDockMode('view')} submitOverride={async ({ payload }) => api.patch(`/ledger-entries/${activeEntryId}`, config.buildHeaderPatchPayload(payload))} />
              ) : (
                <div className="grid gap-3 lg:grid-cols-6"><div className="lg:col-span-2 rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Narration</div><div className="mt-1">{asString(displayEntry, 'narration') || '—'}</div></div><div className="rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Job</div><div className="mt-1">{asString(displayEntry, 'job_number', 'jobId') || '—'}</div></div><div className="rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Customer</div><div className="mt-1">{asString(displayEntry, 'customer_name', 'customerId') || '—'}</div></div><div className="rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Vendor</div><div className="mt-1">{asString(displayEntry, 'vendor_name', 'vendorId') || '—'}</div></div><div className="rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Due Date</div><div className="mt-1">{asString(displayEntry, 'dueDate') || '—'}</div></div><div className="rounded-xl border bg-muted/20 px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Entry Date</div><div className="mt-1">{asString(displayEntry, 'entryDate') || '—'}</div></div></div>
              )}
            </div>

            <div className="rounded-2xl border bg-background p-4">
              <div className="mb-3 flex items-center justify-between gap-3"><div><div className="text-sm font-semibold">Detail Lines</div><div className="text-xs text-muted-foreground">Safe amount fields stay row-editable. Account and tax mapping now edit inline inside this dock instead of a popup.</div></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => openLineEditor()} disabled={!activeEntryPolicy.canAddLine}><FilePlus2 className="mr-2 h-4 w-4" />Add Line</Button><Button size="sm" variant="outline" onClick={() => { if (selectedLine) openLineEditor(selectedLine); }} disabled={!selectedLine || !activeEntryPolicy.canEditLine}><Pencil className="mr-2 h-4 w-4" />Edit Line</Button><Button size="sm" variant="outline" onClick={handleDeleteLine} disabled={!selectedLine || !activeEntryPolicy.canDeleteLine}><Trash2 className="mr-2 h-4 w-4" />Delete Line</Button></div></div>
              {selectedCopiedLineComparison ? (
                <Card className="mb-4 border-sky-200 bg-sky-50/50">
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Copied Line Comparison</div>
                        <div className="text-xs text-slate-600">Source line vs current draft line. The first copied line is auto-selected if you have not chosen one yet.</div>
                      </div>
                      <Badge variant="outline" className="border-sky-200 bg-white text-sky-700">Target #{selectedCopiedLineComparison.targetLineId.slice(0, 8)}</Badge>
                    </div>
                    <div className="grid gap-3 md:grid-cols-5">
                      <div className="rounded-xl border bg-white px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Source Qty</div><div className="mt-1 font-semibold">{selectedCopiedLineComparison.sourceQuantity}</div><div className="mt-1 text-xs text-muted-foreground">Current {selectedCopiedLineComparison.targetQuantity}</div></div>
                      <div className="rounded-xl border bg-white px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Source Price</div><div className="mt-1 font-semibold">{selectedCopiedLineComparison.sourceUnitPrice.toFixed(2)}</div><div className="mt-1 text-xs text-muted-foreground">Current {selectedCopiedLineComparison.targetUnitPrice.toFixed(2)}</div></div>
                      <div className="rounded-xl border bg-white px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Source Amount</div><div className="mt-1 font-semibold">{selectedCopiedLineComparison.sourceAmount.toFixed(2)}</div><div className="mt-1 text-xs text-muted-foreground">Current {selectedCopiedLineComparison.targetAmount.toFixed(2)}</div></div>
                      <div className="rounded-xl border bg-white px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Source Tax %</div><div className="mt-1 font-semibold">{selectedCopiedLineComparison.sourceTaxRate.toFixed(2)}</div><div className="mt-1 text-xs text-muted-foreground">Current {selectedCopiedLineComparison.targetTaxRate.toFixed(2)}</div></div>
                      <div className="rounded-xl border bg-white px-3 py-2 text-sm"><div className="text-xs text-muted-foreground">Source Tax Amount</div><div className="mt-1 font-semibold">{selectedCopiedLineComparison.sourceTaxAmount.toFixed(2)}</div><div className="mt-1 text-xs text-muted-foreground">Current {selectedCopiedLineComparison.targetTaxAmount.toFixed(2)}</div></div>
                    </div>
                    <div className="rounded-xl border bg-white px-3 py-2 text-sm">
                      <div className="text-xs text-muted-foreground">Source Description</div>
                      <div className="mt-1 font-medium">{selectedCopiedLineComparison.sourceDescription || '—'}</div>
                      <div className="mt-2 text-xs text-muted-foreground">Current Description</div>
                      <div className="mt-1">{selectedCopiedLineComparison.targetDescription || '—'}</div>
                    </div>
                    {selectedCopiedLineComparison.warnings.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Pricing Variance Alerts</div>
                        <div className="flex flex-wrap gap-2">
                          {selectedCopiedLineComparison.warnings.map((warning) => (
                            <Badge key={warning} variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{warning}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Pricing matches copied source.</Badge>
                    )}
                  </CardContent>
                </Card>
              ) : null}
              {lineEditorOpen && lineMeta && activeEntryId ? (
                <Card className="mb-4 border-amber-200 bg-amber-50/60">
                  <CardContent className="space-y-3 p-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{editingLine ? `Edit Line ${asString(editingLine, 'lineNo')}` : `Add ${config.documentLabel} Line`}</div>
                      <div className="text-xs text-slate-600">This editor stays inline with the draft so line mapping, amount changes, and recalculated totals remain visible together.</div>
                    </div>
                    <EntityEditForm meta={lineMeta} slug="ledger-lines" templateSlug={config.lineFormTemplateSlug} record={editingLine} initialValues={editingLine ?? { quantity: 1, taxRate: 0 }} stickyActions onSaved={() => { closeLineEditor(); void refreshAll(activeEntryId); toast.success(editingLine ? `${config.documentLabel} line updated` : `${config.documentLabel} line added`); }} onCancel={closeLineEditor} submitOverride={async ({ isEdit, record, payload }) => { if (isEdit && record) { return api.patch(`/ledger-entries/${activeEntryId}/lines/${asString(record, 'id')}`, config.buildLinePayload(payload)); } return api.post(`/ledger-entries/${activeEntryId}/lines`, config.buildLinePayload(payload)); }} />
                  </CardContent>
                </Card>
              ) : null}
              {lineMeta ? (
                <WorkspaceGrid slug="ledger-lines" meta={lineMeta} columns={lineColumns} data={entryLines} total={entryLines.length} page={1} pageSize={Math.max(entryLines.length, 1)} onPageChange={() => undefined} onSort={() => undefined} loading={detailLoading} selectable={false} showSavedViews={false} showDensity={false} onRefresh={() => refreshAll(activeEntryId)} onRowClick={(row) => setSelectedLineId(asString(row, 'id'))} onQuickEdit={(row) => openLineEditor(row)} onCellEditOverride={handleLineCellEdit} rowClassName={(row) => recentCopyHighlight?.entryId === activeEntryId && recentCopyHighlight.lineIds.includes(asString(row, 'id')) ? 'bg-emerald-50/80 ring-1 ring-inset ring-emerald-200' : undefined} toolbarExtra={<div className="text-xs text-muted-foreground">{recentCopyHighlight?.entryId === activeEntryId && recentCopyHighlight.lineIds.length > 0 ? `${recentCopyHighlight.lineIds.length} copied line(s) highlighted • ${copiedLineComparisons.filter((item) => item.warnings.length > 0).length} variance alert(s)` : `Selected line: ${selectedLine ? `#${asString(selectedLine, 'lineNo')}` : 'none'}`}</div>} />
              ) : (
                <div className="text-sm text-muted-foreground">Loading line metadata…</div>
              )}
            </div>
          </div>
        ) : null}
      </RightDockDetailSheet>

      {activeEntryId ? (
        <DocumentCopySheet
          open={copyLinesOpen}
          onOpenChange={setCopyLinesOpen}
          title={`Copy Lines Into ${config.documentLabel}`}
          description="Browse historical quotation, order, or invoice records from the right-side history panel, tick the lines you want, then copy them into this draft financial document."
          targetId={activeEntryId}
          targetLabel={config.documentLabel.toLowerCase()}
          endpoints={{
            listPath: '/ledger-entries/copy-sources',
            detailPath: '/ledger-entries/copy-source-detail',
            copyPath: '/ledger-entries/copy',
          }}
          onCopied={async (copyResult, sourceDetail) => {
            setRecentCopyHighlight({
              entryId: activeEntryId,
              lineIds: copyResult.copiedLineIds,
            });
            setRecentCopyContext({
              entryId: activeEntryId,
              copyResult,
              sourceDetail,
            });
            setSelectedLineId(copyResult.copiedLineIds[0] ?? null);
            await refreshAll(activeEntryId);
            toast.success(`${copyResult.copiedLineCount} ${config.documentLabel.toLowerCase()} line(s) copied`);
          }}
        />
      ) : null}
    </FinanceWorkspaceShell>
  );
}

export function buildStandardEntryPayload(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    entryNumber: String(payload.entryNumber ?? '').trim(),
    entryType: String(payload.entryType ?? 'AR'),
    jobId: asNullableString(payload.jobId),
    customerId: asNullableString(payload.customerId),
    vendorId: asNullableString(payload.vendorId),
    currency: String(payload.currency ?? 'SGD').trim() || 'SGD',
    exchangeRate: Number(payload.exchangeRate ?? 1) || 1,
    entryDate: asNullableString(payload.entryDate),
    dueDate: asNullableString(payload.dueDate),
    narration: asNullableString(payload.narration),
  };
}

export function buildBillingHeaderPatchPayload(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    dueDate: asNullableString(payload.dueDate),
    narration: asNullableString(payload.narration),
  };
}

export function buildJournalHeaderPatchPayload(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    entryDate: asNullableString(payload.entryDate),
    narration: asNullableString(payload.narration),
  };
}

export function buildBillingLinePayload(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    lineNo: payload.lineNo ? Number(payload.lineNo) : undefined,
    accountId: asNullableString(payload.accountId) ?? undefined,
    chargeCodeId: asNullableString(payload.chargeCodeId) ?? undefined,
    description: String(payload.description ?? '').trim(),
    quantity: Number(payload.quantity ?? 1) || 1,
    unitPrice: Number(payload.unitPrice ?? 0) || 0,
    taxCodeId: asNullableString(payload.taxCodeId) ?? undefined,
    taxRate: Number(payload.taxRate ?? 0) || 0,
  };
}

export function buildJournalLinePayload(payload: Record<string, unknown>): Record<string, unknown> {
  return {
    lineNo: payload.lineNo ? Number(payload.lineNo) : undefined,
    accountId: asNullableString(payload.accountId) ?? undefined,
    description: String(payload.description ?? '').trim(),
    debit: Number(payload.debit ?? 0) || undefined,
    credit: Number(payload.credit ?? 0) || undefined,
  };
}
